import { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box, Center, Flex } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, Text, Title } from "components";
import { AuthorityConstants, ServiceConstants } from "constant";
import { IRootState } from "config/redux";
import { BankCreditService, CreditService, PreliminaryService } from "services";
import {
  BankCreditResponse,
  CreditOptionsResponse,
  CreditResponse,
  PreliminaryCredit,
  UserResponse,
} from "types";
import { AppUtils, UserUtils } from "utils";

import CreditHeader from "./header";
import { CreditComparisonSource } from "./request/comparison-source";
import CreditTabs, { CreditTabsHandle } from "./tabs";

const { ROLE_CR_C } = AuthorityConstants;
const {
  REQUEST_CREDIT_PREVIOUS_LAST_ID,
  REQUEST_PRELIMINARY_CREDIT_VIEW_ID,
  REQUEST_PRELIMINARY_CREDIT_NEW_ID,
} = ServiceConstants;

interface ClientCreditProps {
  credit?: CreditResponse;
  bankCredit?: BankCreditResponse;
  options?: CreditOptionsResponse;
  user: UserResponse;
  onPreliminaryChange?: (isPreliminary: boolean) => void;
}

const ClientCreditComponent = ({
  credit,
  bankCredit,
  options,
  user,
  onPreliminaryChange,
}: ClientCreditProps) => {
  const { t } = useTranslation();

  const preliminaryServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_PRELIMINARY_CREDIT_VIEW_ID],
  );
  const preliminaryNewServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_PRELIMINARY_CREDIT_NEW_ID],
  );
  const previousCreditLastServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_PREVIOUS_LAST_ID],
  );
  const [preliminary, setPreliminary] = useState<PreliminaryCredit | null>(null);
  const [preliminaryResolved, setPreliminaryResolved] = useState(false);
  const [previousCredit, setPreviousCredit] = useState<CreditResponse | null>(null);
  const [previousCreditResolved, setPreviousCreditResolved] = useState(false);
  const creditTabsRef = useRef<CreditTabsHandle>(null);

  useEffect(() => {
    setPreliminary(null);
    setPreliminaryResolved(false);
    setPreviousCredit(null);
    setPreviousCreditResolved(false);

    PreliminaryService.preliminaryGetByUser(user.id);
  }, [credit, user.id]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      preliminaryServiceObject,
      REQUEST_PRELIMINARY_CREDIT_VIEW_ID,
      (val: PreliminaryCredit | null | undefined) => {
        setPreliminary(val ?? null);
        setPreliminaryResolved(true);
      },
      () => {
        setPreliminary(null);
        setPreliminaryResolved(false);
      },
    );
    AppUtils.resolveServiceObject(
      preliminaryNewServiceObject,
      REQUEST_PRELIMINARY_CREDIT_NEW_ID,
      (val: PreliminaryCredit | null | undefined) => {
        setPreliminary(val ?? null);
        setPreliminaryResolved(true);
      },
      () => PreliminaryService.preliminaryGetByUser(user.id),
    );
  }, [preliminaryServiceObject, preliminaryNewServiceObject, user.id]);

  useEffect(() => {
    onPreliminaryChange?.(!credit && !!preliminary);
  }, [credit, onPreliminaryChange, preliminary]);

  useEffect(() => {
    if (!credit || !preliminaryResolved || preliminary) {
      return;
    }

    setPreviousCredit(null);
    setPreviousCreditResolved(false);
    CreditService.creditsPreviousLast(user.id);
  }, [credit, preliminary, preliminaryResolved, user.id]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      previousCreditLastServiceObject,
      REQUEST_CREDIT_PREVIOUS_LAST_ID,
      (val: CreditResponse | null | undefined) => {
        setPreviousCredit(val ?? null);
        setPreviousCreditResolved(true);
      },
      () => {
        setPreviousCredit(null);
        setPreviousCreditResolved(true);
      },
    );
  }, [previousCreditLastServiceObject]);

  const comparisonSource: CreditComparisonSource | null = credit
    ? preliminary
      ? { type: "preliminary", credit: preliminary }
      : preliminaryResolved && previousCreditResolved && previousCredit
        ? { type: "previous", credit: previousCredit }
        : null
    : null;

  const createNewCredit = useCallback(() => {
    CreditService.creditsNew(user.id);
  }, [user]);

  const createPreliminaryCredit = useCallback(() => {
    PreliminaryService.preliminaryCreate(user.id);
  }, [user.id]);

  const validateRequiredFields = useCallback(() => {
    if (creditTabsRef.current?.hasMissingRequiredFields()) {
      notifications.show({
        color: "red",
        message: t("credit.new.validation.requiredFields"),
      });
      return false;
    }

    return true;
  }, [t]);

  const submitCreditRequest = useCallback(() => {
    if (!validateRequiredFields()) {
      return;
    }

    if (credit?.id) {
      BankCreditService.bankCreditNew(credit.id);
    }
  }, [credit?.id, validateRequiredFields]);

  if (!credit || !options) {
    if (preliminary && options) {
      return (
        <Box pb="xl">
          {UserUtils.hasAuthority(ROLE_CR_C) && (
            <Flex mx={"sm"} mb={"sm"}>
              <Button onClick={createNewCredit}>{t("credit.new.title")}</Button>
            </Flex>
          )}
          <CreditTabs
            ref={creditTabsRef}
            key={`preliminary-${preliminary.id}`}
            credit={preliminary}
            options={options}
            updateService={PreliminaryService.preliminaryUpdateMultiple}
            isPreliminary={true}
          />
        </Box>
      );
    }

    return (
      <Box>
        {UserUtils.hasAuthority(ROLE_CR_C) && (
          <Flex gap={"sm"} mx={"sm"}>
            <Button variant={"outline"} onClick={createPreliminaryCredit}>
              {t("credit.preliminary.title")}
            </Button>
            <Button onClick={createNewCredit}>{t("credit.new.title")}</Button>
          </Flex>
        )}
        <Center>
          <Box my={64} w={450}>
            <Title order={4} ta={"center"}>
              {t("credit.view.empty.title")}
            </Title>
            <Text ta={"center"}>{t("credit.view.empty.description")}</Text>
          </Box>
        </Center>
      </Box>
    );
  }

  return (
    <Box pb="xl">
      <CreditHeader credit={credit} onRequestDocumentation={validateRequiredFields} />
      <CreditTabs
        ref={creditTabsRef}
        key={`credit-${credit.id}`}
        credit={credit}
        bankCredit={bankCredit}
        options={options}
        readOnly={!!credit.bankCreditRequestId}
        comparisonSource={comparisonSource}
      />

      {UserUtils.hasAuthority(ROLE_CR_C) && !credit.bankCreditRequestId && (
        <Flex mt="md" justify="flex-end" pr="md">
          <Button onClick={submitCreditRequest} type="button">
            {t("credit.submit")}
          </Button>
        </Flex>
      )}
    </Box>
  );
};

const ClientCredit = memo(ClientCreditComponent);

export default ClientCredit;
